import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

import { formatUSPhone } from "src/Common/function";
import AddressInput from "src/Components/Common/addressinput";
import UserService from "src/Services/API/user.service";
import { SelectDropdown } from "./components";

/**
 * Single-location modal. Add and Edit share the same layout; the only
 * differences are the title/button label and which callback fires.
 *  - Add Location: requires `presetAccountId` (opened from a customer's page).
 *    `accountName` is shown as a fixed "Customer Name -" prefix on the
 *    location name so new locations are searchable by customer.
 *  - Edit Location: `customer` is provided and pre-fills the form.
 */
function LocationAddModal({
  branches = [],
  customers = [],
  customer,
  presetAccountId = null,
  accountName = "",
  onAddClicked,
  onUpateClicked,
  onCancelClicked,
}) {
  const currentUser = UserService.getCurrentUser();
  const isEditMode = Boolean(customer);
  const namePrefix = isEditMode ? "" : (accountName || "").trim();

  const [fields, setFields] = useState({
    companyName: "",
    contact: "",
    email: "",
    phone: "",
    address: "",
    branch: null,
  });

  const [submitted, setSubmitted] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);

  useEffect(() => {
    if (customer) {
      setFields({
        companyName: customer.companyName || "",
        contact: customer.contact || "",
        email: customer.email || "",
        phone: formatUSPhone(customer.phone || ""),
        address: customer.address || "",
        branch: customer.branch || null,
      });
    }
  }, [customer]);

  const updateField = (patch) => setFields((f) => ({ ...f, ...patch }));

  const submit = () => {
    setSubmitted(true);

    const typed = fields.companyName.trim();

    // Every field except address is required.
    if (!typed || !fields.contact.trim() || !fields.email.trim() || !fields.phone.trim() || !fields.branch) return;

    const name = namePrefix ? `${namePrefix} - ${typed}` : typed;

    const normalized = name.toLowerCase();
    const isDuplicate = customers.some(
      (item) => item.id !== customer?.id && item.companyName?.trim().toLowerCase() === normalized,
    );
    if (isDuplicate) {
      setDuplicateError(true);
      return;
    }

    if (isEditMode) {
      onUpateClicked({
        ...customer,
        companyName: name,
        contact: fields.contact,
        email: fields.email,
        phone: fields.phone,
        address: fields.address,
        branch: fields.branch,
      });
      return;
    }

    // Add flow — the customer is fixed via presetAccountId.
    if (!presetAccountId) return;

    onAddClicked({
      accountId: presetAccountId,
      locations: [
        {
          companyName: name,
          contact: fields.contact,
          email: fields.email,
          phone: fields.phone,
          address: fields.address,
          branch: fields.branch,
          createdById: currentUser?.id,
        },
      ],
    });
  };

  return (
    <div
      onClick={onCancelClicked}
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[672px] max-w-full max-h-[90vh] bg-white rounded-[12px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E8F3]">
          <div className="flex flex-col">
            <h2 className="text-[20px] leading-[28px] capitalize font-OpenSansSemiBold text-[#1A2332]">
              {isEditMode ? "Edit Location" : "New Location"}
            </h2>
            <p className="text-[14px] leading-[20px] font-OpenSansRegular text-[#868DA6]">
              Add a new service location to an existing customer account.
            </p>
          </div>
          <FiX size={18} className="text-[#868DA6] cursor-pointer" onClick={onCancelClicked} />
        </div>

        <div className="p-8 overflow-y-auto flex-1">
          <div className="flex flex-col gap-4">
            <Field
              label="Location Name"
              required
              prefix={namePrefix}
              value={fields.companyName}
              onChange={(v) => {
                updateField({ companyName: v });
                if (duplicateError) setDuplicateError(false);
              }}
              placeholder="Enter location name"
              error={(submitted && !fields.companyName.trim()) || duplicateError}
            />
            {duplicateError && (
              <p className="text-[12px] text-red-500 -mt-3 font-OpenSansRegular">
                A location with this name already exists.
              </p>
            )}
            <Field
              label="Contact Name"
              required
              value={fields.contact}
              onChange={(v) => updateField({ contact: v })}
              placeholder="Enter contact name"
              error={submitted && !fields.contact.trim()}
            />
            <Field
              label="Email"
              required
              type="email"
              value={fields.email}
              onChange={(v) => updateField({ email: v })}
              placeholder="contact@example.com"
              error={submitted && !fields.email.trim()}
            />
            <Field
              label="Phone Number"
              required
              value={fields.phone}
              onChange={(v) => updateField({ phone: formatUSPhone(v) })}
              placeholder="(808) 555-0100"
              error={submitted && !fields.phone.trim()}
            />
            <div className="flex flex-col gap-1">
              <label className="block text-[14px] leading-[20px] tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                Address
              </label>
              <AddressInput value={fields.address} onChange={(v) => updateField({ address: v })} />
            </div>
            <SelectDropdown
              label="Branch"
              required
              placeholder="Select branch"
              value={fields.branch}
              options={branches}
              getLabel={(b) => b.name}
              getKey={(b) => b.id}
              onSelect={(b) => updateField({ branch: b })}
              error={submitted && !fields.branch}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-[#E5E8F3]">
          <button
            type="button"
            onClick={onCancelClicked}
            className="h-[40px] px-1 rounded-[6px] text-[16px] leading-[24px] capitalize font-OpenSansSemiBold text-[#7C829A]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="h-[40px] px-4 rounded-[8px] text-[16px] leading-[24px] capitalize font-OpenSansSemiBold bg-blueColor text-white"
          >
            {isEditMode ? "Save Changes" : "Add Location"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, type = "text", error, prefix }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="block text-[14px] leading-[20px] tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {prefix ? (
        <div
          className={`w-full h-[40px] flex items-center rounded-[6px] border focus-within:border-blueColor ${
            error ? "border-red-500" : "border-[#E5E8F3]"
          }`}
        >
          <span className="pl-3 max-w-[55%] truncate whitespace-nowrap select-none text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#868DA6]">
            {prefix} -
          </span>
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 min-w-0 h-full pl-1 pr-3 rounded-[6px] outline-none text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#1A2332]"
          />
        </div>
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full h-[40px] px-3 rounded-[6px] border text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#1A2332] focus:border-blueColor ${
            error ? "border-red-500" : "border-[#E5E8F3]"
          }`}
        />
      )}
    </div>
  );
}

export default LocationAddModal;
